// The client's review page (/r/<token>): stars, the crew, then on to Google or a word to the office.
import { clientLinkTitle, type ReviewPagePublic, type ReviewRateAnswer } from "@steward/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import { shortDate } from "../../components/format";
import { Banner, Button, Skeleton } from "../../components/parts";
import { errText } from "../../components/work";
import { Branded, NotFound } from "./common";

/**
 * The client's review page: tap 1 to 5 stars. 4 or 5 go straight on to Google. 1 to 3 get a note box so the owner can
 * make it right, and no Google button (owner, Oct 30). The page names the job, the day and the crew with their pictures,
 * and shows the brand's Google rating (owner, 2026-11-02: "it seems so bland and nothing there, maybe a picture of the
 * team"). A step to tap what stood out and copy words before Google was tried and taken out the same night (owner: "i
 * dont think it would do good and adds friction").
 */
const STAR_WORDS = ["", "Not good", "Could be better", "Okay", "Great", "Loved it"];

const namesOf = (crew: { first: string }[]) =>
  crew.length <= 1
    ? (crew[0]?.first ?? "")
    : `${crew
        .slice(0, -1)
        .map((c) => c.first)
        .join(", ")} and ${crew.at(-1)!.first}`;

function CrewFaces({
  token,
  crew,
  color,
}: {
  token: string;
  crew: { id: string; first: string; photo: boolean }[];
  color: string;
}) {
  if (crew.length === 0) return null;
  return (
    <div className="rv-faces" aria-hidden="true">
      {crew.slice(0, 4).map((c) =>
        c.photo ? (
          <img key={c.id} src={`/v1/public/review/${token}/crew/${c.id}`} alt="" />
        ) : (
          <span key={c.id} style={{ background: color }}>
            {c.first.charAt(0).toUpperCase()}
          </span>
        ),
      )}
    </div>
  );
}

export function ReviewPage({ token }: { token: string }) {
  const r = useQuery({
    queryKey: ["public-review", token],
    queryFn: () => api<ReviewPagePublic>("GET", `public/review/${token}`),
  });
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState("");
  const [step, setStep] = useState<"pick" | "note" | "thanks" | "going">("pick");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (r.error) return <NotFound what="page" />;
  const d = r.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={4} />
      </Branded>
    );
  const crew = d.crew ?? [];
  const crewNames = namesOf(crew);
  const color = d.look?.accent ?? d.brandColor ?? "#1f6f5c";
  const send = async (n: number, note?: string) => {
    setBusy(true);
    setErr(null);
    try {
      const a = await api<ReviewRateAnswer>("POST", `public/review/${token}`, { stars: n, ...(note ? { note } : {}) });
      if (n >= 4 && a.go) {
        setStep("going");
        window.location.assign(a.go);
      } else setStep(n >= 4 || note !== undefined ? "thanks" : "note");
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const pick = (n: number) => {
    setStars(n);
    void send(n);
  };
  const shown = hover || stars;
  return (
    <Branded
      title={clientLinkTitle("review", { brand: d.business, first: d.firstName }).title}
      brand={{
        name: d.business,
        color: d.brandColor,
        look: d.look,
        phone: null,
        email: null,
        links: [],
        address: null,
        website: null,
      }}
    >
      <div className="rv">
        {step === "going" ? (
          <>
            <CrewFaces token={token} crew={crew} color={color} />
            <h1>Thank you, {d.firstName}!</h1>
            <p className="rv-lead">Opening Google so you can share it there.</p>
          </>
        ) : step === "thanks" ? (
          <>
            <h1>Thank you, {d.firstName}</h1>
            <p>We read every note. Someone from {d.business} will reach out soon.</p>
          </>
        ) : step === "note" ? (
          <>
            <h1>We are sorry, {d.firstName}</h1>
            <p>Tell us what went wrong so we can make it right.</p>
            <textarea
              rows={5}
              maxLength={2000}
              value={note}
              aria-label="What went wrong"
              onChange={(e) => setNote(e.target.value)}
            />
            {err ? <Banner type="error">{err}</Banner> : null}
            <Button size="large" fullWidth loading={busy} onClick={() => void send(stars, note.trim())}>
              Send
            </Button>
          </>
        ) : (
          <>
            <CrewFaces token={token} crew={crew} color={color} />
            {d.job ? (
              <p className="rv-eyebrow">
                Your {d.job}
                {d.day ? ` on ${shortDate(d.day)}` : ""}
              </p>
            ) : null}
            <h1>How did we do, {d.firstName}?</h1>
            <p className="rv-lead">
              {crewNames ? `${crewNames} would love to hear from you.` : "We would love to hear from you."} It takes 20 seconds.
            </p>
            <div className="review-stars" role="radiogroup" aria-label="Your rating" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={stars === n}
                  aria-label={`${String(n)} star${n === 1 ? "" : "s"}, ${STAR_WORDS[n]!}`}
                  className={n <= shown ? "on" : ""}
                  disabled={busy}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => pick(n)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6-4.9-4.6 6.6-.8z" />
                  </svg>
                </button>
              ))}
            </div>
            <p className="rv-word" aria-live="polite">
              {shown ? STAR_WORDS[shown] : "Tap a star"}
            </p>
            {err ? <Banner type="error">{err}</Banner> : null}
            {d.google ? (
              <p className="rv-proof">
                <span aria-hidden="true">★</span> {d.google.rating.toFixed(1)} on Google from {d.google.reviews.toLocaleString()}{" "}
                neighbors
              </p>
            ) : null}
          </>
        )}
      </div>
    </Branded>
  );
}
